/**
 * Identity guard which always returns true
 * @returns true
 */
export const always = () => true;

/**
 * Negates the supplied guards.
 * @example ```typescript
 *  const yeah = () => true;
 *  const yeahnah = not(yeah);
 *
 *  yeahnah(); // → false (or does it?)
 *```
 */
export const not = (guard: (...args: any[]) => boolean) => {
  const fn = (...args: any[]) => !guard(...args);
  fn.combinator = 'not';
  fn.guard = guard;
  return fn;
};

/**
 * Log the guard name and its result for debugging purposes.
 * @example ```typescript
 *  const yeah = () => true;
 *  const yeahnah = not(yeah);
 *
 *  debug(yeahnah)(); // { guard: 'yeannah', passed: true }
 *```
 */
export const debug =
  (guard: (...args: any[]) => boolean) =>
  (...args: any[]) => {
    const result = guard(...args);
    console.log({ guard: guard.name, passed: result, args });
    return result;
  };

/**
 * ORs the supplied guards..
 * @example ```typescript
 *  const yeah = () => true;
 *  const nah = () => false;
 *
 *  const yeahnah = or(yeah, nah);
 *
 *  yeahnah(); // → true (or... does it?)
 *```
 */
export const or = (...guards: ((...args: any[]) => boolean)[]) => {
  const fn = (...args: any[]) => guards.some((guard) => guard(...args));
  fn.combinator = 'or';
  fn.guards = guards;
  return fn;
};

/**
 * ANDs the supplied guards..
 * @example ```typescript
 *  const yeah = () => true;
 *  const nah = () => false;
 *
 *  const yeahnah = and(yeah, nah);
 *
 *  yeahnah(); // → false
 *```
 */
export const and = (...guards: ((...args: any[]) => boolean)[]) => {
  const fn = (...args: any[]) => guards.every((guard) => guard(...args));
  fn.combinator = 'and';
  fn.guards = guards;
  return fn;
};

/**
 * Makes a guard optional - if the guard is not provided, returns true
 */
export const optional =
  (guard?: (...args: any[]) => boolean) =>
  (...args: any[]) => {
    if (!guard) {
      return true;
    }
    return guard(...args);
  };
